import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { riderService } from '../../../services';

interface VehicleUnassignmentDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (
    reason: string,
    riderFeedback?: string,
    images?: Record<string, File | null>,
    returningOdometerCount?: number
  ) => Promise<void>;
  vehicleInfo?: {
    registrationNumber: string;
    make?: string;
    model?: string;
  };
  pendingAmount?: number;
  publicRiderId?: string;
  riderProfileId?: string;
}

const ASSIGNMENT_IMAGE_MAX_SIZE_MB = 10;
const ASSIGNMENT_IMAGE_MAX_SIZE_BYTES = ASSIGNMENT_IMAGE_MAX_SIZE_MB * 1024 * 1024;
const ASSIGNMENT_IMAGE_SIZE_MESSAGE =
  `File size is too big. Make sure each image is less than ${ASSIGNMENT_IMAGE_MAX_SIZE_MB} MB.`;

const VehicleUnassignmentDialog: React.FC<VehicleUnassignmentDialogProps> = ({
  open,
  onClose,
  onConfirm,
  vehicleInfo,
  pendingAmount = 0,
  publicRiderId,
  riderProfileId,
}) => {
  const navigate = useNavigate();
  const [frontImage, setFrontImage] = useState<File | null>(null);
  const [backImage, setBackImage] = useState<File | null>(null);
  const [leftImage, setLeftImage] = useState<File | null>(null);
  const [rightImage, setRightImage] = useState<File | null>(null);
  const [odometerImage, setOdometerImage] = useState<File | null>(null);
  const [virtualImage, setVirtualImage] = useState<File | null>(null);
  const [chargerImage, setChargerImage] = useState<File | null>(null);
  const [batteryImage, setBatteryImage] = useState<File | null>(null);
  const [returningOdometerCount, setReturningOdometerCount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forceUnassignConfirmed, setForceUnassignConfirmed] = useState(false);
  const [totalEarning, setTotalEarning] = useState<number | null>(null);

  const requiredImagesSelected = Boolean(
    frontImage && backImage && leftImage && rightImage && odometerImage && virtualImage
  );

  const [cameraField, setCameraField] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraSetter, setCameraSetter] = useState<React.Dispatch<React.SetStateAction<File | null>> | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const stopCameraStream = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [cameraStream]);

  const handleCloseCamera = useCallback(() => {
    stopCameraStream();
    setCameraField(null);
    setCameraSetter(null);
    setCameraError(null);
    setCameraReady(false);
  }, [stopCameraStream]);

  const handleTakePhoto = async (
    field: string,
    setter: React.Dispatch<React.SetStateAction<File | null>>
  ) => {
    if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera access is not available in this browser.');
      return;
    }

    setCameraError(null);
    setCameraReady(false);
    setCameraField(field);
    setCameraSetter(() => setter);

    try {
      stopCameraStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });

      setCameraStream(stream);
    } catch (error) {
      console.error('Camera capture failed', error);
      setCameraError('Unable to open the camera. Please try the upload option instead.');
      handleCloseCamera();
    }
  };

  const handleCapturePhoto = useCallback(() => {
    if (!videoRef.current || !cameraSetter) {
      setCameraError('Camera preview is not ready yet.');
      return;
    }

    const video = videoRef.current;
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      setCameraError('Unable to access the camera stream.');
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) {
        setCameraError('Failed to capture the image.');
        return;
      }

      const file = new File([blob], `camera-${Date.now()}.jpg`, {
        type: 'image/jpeg',
      });
      cameraSetter(file);
      handleCloseCamera();
    }, 'image/jpeg', 0.95);
  }, [cameraSetter, handleCloseCamera]);

  useEffect(() => {
    if (!cameraStream || !videoRef.current || !cameraField) {
      return;
    }

    const video = videoRef.current;
    video.srcObject = cameraStream;

    const handleReady = () => setCameraReady(true);
    video.addEventListener('canplay', handleReady);

    const playPromise = video.play();
    if (video.readyState >= 3) {
      setCameraReady(true);
    }

    if (playPromise?.catch) {
      playPromise.catch((err) => {
        console.error('Camera play failed', err);
        setCameraError('Unable to open the camera.');
        handleCloseCamera();
      });
    }

    return () => {
      video.removeEventListener('canplay', handleReady);
    };
  }, [cameraStream, cameraField, handleCloseCamera]);

  const handleClose = () => {
    if (!loading) {
      handleCloseCamera();
      setFrontImage(null);
      setBackImage(null);
      setLeftImage(null);
      setRightImage(null);
      setOdometerImage(null);
      setVirtualImage(null);
      setChargerImage(null);
      setBatteryImage(null);
      setReturningOdometerCount('');
      setForceUnassignConfirmed(false);
      setError(null);
      onClose();
    }
  };

  const handleConfirm = async () => {
    const odometerCount = returningOdometerCount === '' ? 0 : Number(returningOdometerCount);
    if (!Number.isInteger(odometerCount) || odometerCount < 0) {
      setError('Odometer count must be a non-negative whole number.');
      return;
    }
    if (odometerCount > 999999) {
      setError('Odometer count should less then or equal to 999999.');
      return;
    }
    if (!requiredImagesSelected) {
      setError('Please upload the six required images before unassigning the vehicle. Charger and battery images are optional.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onConfirm('', undefined, {
        frontImage,
        backImage,
        leftImage,
        rightImage,
        odometerImage,
        virtualImage,
        chargerImage,
        batteryImage,
      }, odometerCount);
      // Close dialog on success
      setFrontImage(null);
      setBackImage(null);
      setLeftImage(null);
      setRightImage(null);
      setOdometerImage(null);
      setReturningOdometerCount('');
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to unassign vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleImageFileChange = (
    file: File | undefined,
    setter: React.Dispatch<React.SetStateAction<File | null>>
  ) => {
    if (!file) {
      setter(null);
      return;
    }

    if (file.size > ASSIGNMENT_IMAGE_MAX_SIZE_BYTES) {
      setter(null);
      setError(ASSIGNMENT_IMAGE_SIZE_MESSAGE);
      return;
    }

    setError(null);
    setter(file);
  };

  const formattedPendingAmount = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number.isFinite(pendingAmount) ? pendingAmount : 0);
  const formattedTotalEarning = totalEarning === null
    ? '-'
    : new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(totalEarning);
  const hasPendingAmount = Number.isFinite(pendingAmount) && pendingAmount > 0;

  useEffect(() => {
    let cancelled = false;

    if (!open || !publicRiderId) {
      setTotalEarning(null);
      return () => {
        cancelled = true;
      };
    }

    const fetchTotalEarning = async () => {
      try {
        const response = await riderService.getRentValue(publicRiderId);
        const earning = Number(response?.data?.TotalEarning);
        if (!cancelled) {
          setTotalEarning(Number.isFinite(earning) ? earning : null);
        }
      } catch (error) {
        console.error('Failed to fetch rider total earning:', error);
        if (!cancelled) {
          setTotalEarning(null);
        }
      }
    };

    void fetchTotalEarning();

    return () => {
      cancelled = true;
    };
  }, [open, publicRiderId]);

  useEffect(() => {
    if (open) {
      setReturningOdometerCount('');
      setError(null);
    }
  }, [open]);

  const handleCollectDeficit = () => {
    if (!publicRiderId) {
      navigate('/manual-collections', { state: { pendingTab: 1 } });
      return;
    }

    navigate('/manual-collections', {
      state: {
        pendingTab: 1,
        riderPublicId: publicRiderId,
        pending: Number.isFinite(pendingAmount) ? pendingAmount : 0,
        returnToRiderProfile: Boolean(riderProfileId),
        riderProfileId,
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Unassign Vehicle
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          You are about to unassign the following vehicle from this rider:
        </DialogContentText>

        {vehicleInfo && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <strong>Registration Number:</strong>
              <span>{vehicleInfo.registrationNumber}</span>
            </Box>
            {vehicleInfo.make && vehicleInfo.model && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong>Vehicle:</strong>
                <span>{vehicleInfo.make} {vehicleInfo.model}</span>
              </Box>
            )}
          </Box>
        )}

        <TextField
          type="number"
          label="Returning Odometer Count"
          value={returningOdometerCount}
          onChange={(event) => setReturningOdometerCount(event.target.value)}
          inputProps={{ min: 0, max: 999999, step: 1 }}
          error={Number(returningOdometerCount) > 999999}
          helperText={Number(returningOdometerCount) > 999999
            ? 'Odometer count should less then or equal to 999999.'
            : 'Enter the odometer reading at vehicle return'}
          fullWidth
          sx={{ mb: 2 }}
        />

        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'warning.light',
            background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 152, 0, 0.06))',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, color: 'warning.dark' }}>
                  Total pending amount
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: 'warning.dark' }}>
                  {formattedPendingAmount}
                </Typography>
              </Box>
              <Box sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'rgba(22, 163, 74, 0.3)' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, color: 'success.dark' }}>
                  Approximate Total Earning
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: 'success.dark' }}>
                  {formattedTotalEarning}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
                backgroundColor: 'rgba(255, 193, 7, 0.12)',
                border: '1px solid',
                borderColor: 'warning.main',
                color: 'warning.dark',
                fontWeight: 700,
                fontSize: '0.75rem',
              }}
            >
              Review before unassignment
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            color="warning"
            onClick={handleCollectDeficit}
            sx={{ justifyContent: 'center', fontWeight: 700 }}
          >
            Click here to collect deficit amount
          </Button>
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={forceUnassignConfirmed}
                onChange={(event) => setForceUnassignConfirmed(event.target.checked)}
                color="warning"
                disabled={!hasPendingAmount}
              />
            }
            label="If rider is unable to pay the pending amount, I confirm to force unassign the vehicle."
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <DialogContentText sx={{ mb: 1 }}>
            Unassignment images (required):
          </DialogContentText>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The six core images are required before confirming unassignment. Charger and battery images are optional.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
              },
            }}
          >
            {[
              { label: 'Front Image', file: frontImage, setter: setFrontImage },
              { label: 'Back Image', file: backImage, setter: setBackImage },
              { label: 'Left Image', file: leftImage, setter: setLeftImage },
              { label: 'Right Image', file: rightImage, setter: setRightImage },
              { label: 'Rider with Vehicle Image', file: odometerImage, setter: setOdometerImage },
              { label: 'Virtual Document Image', file: virtualImage, setter: setVirtualImage },
              { label: 'Charger Image', file: chargerImage, setter: setChargerImage },
              { label: 'Battery Image', file: batteryImage, setter: setBatteryImage },
            ].map(({ label, file, setter }) => {
              const isCameraActive = cameraField === label;
              return (
                <Box
                  key={label}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 1,
                    border: isCameraActive ? '2px solid' : '1px solid',
                    borderColor: isCameraActive ? 'primary.main' : 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {label}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      disabled={loading}
                      sx={{ textTransform: 'none', flex: 1 }}
                      onClick={() => handleTakePhoto(label, setter)}
                    >
                      {file ? 'Retake photo' : 'Open live camera'}
                    </Button>
                    <Button
                      component="label"
                      variant="outlined"
                      disabled={loading}
                      sx={{ textTransform: 'none', flex: 1 }}
                    >
                      {file ? 'Change file' : 'Upload'}
                      <input
                        hidden
                        accept="image/*"
                        type="file"
                        onChange={(e) => {
                          handleImageFileChange(e.target.files?.[0], setter);
                          e.target.value = '';
                        }}
                      />
                    </Button>
                  </Box>
                  {isCameraActive && (
                    <Box sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                      <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 700 }}>
                        Live camera preview
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Position it clearly and tap capture when ready.
                      </Typography>
                      {cameraError && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                          {cameraError}
                        </Alert>
                      )}
                      <Box
                        sx={{
                          position: 'relative',
                          overflow: 'hidden',
                          borderRadius: 1,
                          bgcolor: 'black',
                          minHeight: 220,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          onCanPlay={() => setCameraReady(true)}
                          onLoadedMetadata={() => setCameraReady(true)}
                          onPlaying={() => setCameraReady(true)}
                          style={{
                            width: '100%',
                            maxHeight: 220,
                            objectFit: 'cover',
                            display: cameraReady ? 'block' : 'none',
                          }}
                        />
                        {!cameraReady && (
                          <Typography variant="body2" color="white">
                            Starting camera…
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                        <Button variant="contained" fullWidth onClick={handleCapturePhoto} disabled={!cameraReady}>
                          Capture photo
                        </Button>
                        <Button variant="outlined" fullWidth onClick={handleCloseCamera}>
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {file ? file.name : 'No image selected'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={loading || !requiredImagesSelected || (hasPendingAmount && !forceUnassignConfirmed)}
        >
          {loading ? 'Unassigning...' : 'Confirm Unassignment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VehicleUnassignmentDialog;
